import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/**
 * One row of a "Sign as:" / "Remove as:" dropdown. Every active person who
 * could sign in the tenant is listed; `eligible: false` rows carry the reason
 * and cannot be picked, so the operator sees a restriction before choosing
 * rather than after typing a password (z8nrz7dt3z AC2).
 */
export interface SignerOption {
  id: string
  name: string
  eligible: boolean
  reason: string | null
}

// The server's own message is what the signer needs to read ("User B must
// re-enter their own password", "Not in the QA Review group"), so it is
// surfaced verbatim rather than wrapped in a status line.
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) {
        message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
      }
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

/**
 * The "Sign as" surface (z8nrz7dt3z). Tenant-scoped: `authHeaders` carries the
 * selected tenant, which is what bounds whose password may be checked.
 */
export const signingApi = {
  /**
   * Active people in the tenant, alphabetical, marked eligible or not for a
   * signature that needs `permission`. Form-field and completion signatures
   * have their own roster on formsApi, which also applies the field's groups.
   */
  listSigners: (permission?: string): Promise<SignerOption[]> => {
    const qs = permission ? `?${new URLSearchParams({ permission })}` : ''
    return request<SignerOption[]>(`/signing/signers${qs}`)
  },

  /**
   * Exchange the PICKED person's password for a signing token that records
   * them as the signer and can only be spent in this session. Throttled per
   * target account as well as per session.
   */
  createSigningTokenAs: (
    userId: string,
    password: string,
  ): Promise<{ token: string; expiresIn: number }> =>
    request<{ token: string; expiresIn: number }>('/signing/tokens/as', {
      method: 'POST',
      body: JSON.stringify({ userId, password }),
    }),
}
